import {
  KEY_DOWN,
  KEY_ENTER,
  KEY_LEFT,
  KEY_RETURN,
  KEY_RIGHT,
  KEY_SPACE,
  KEY_UP,
} from 'keycode-js';
import React, {
  KeyboardEventHandler,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Box } from '@mui/material';
import AvatarContainer from '@avatar/components/AvatarContainer';
import Button from '@ui/Button';
import useAudioPlayer from '../../../../lib/useAudioPlayer';
import { StudioClipContext, StudioClipProps } from '../../StudioClip';
import { ClipObject, ClipObjectPending } from '../../../../context/clips';
import Playback from './Playback';
import ClipBodyTop from './ClipBodyTop';
import SliderWrapper, { SliderWrapperProps } from './SliderWrapper';
import { selectText, useAppSelector } from '../../../../store/hooks';
import { useEventsContext } from '../../../../context/events/EventsContext';
import { EventName } from '../../../../services/eventTrackingServiceTypes';

export interface CompleteProps extends Omit<StudioClipProps, 'clip'> {
  setFocusedState: (focused: boolean) => void;
}
/**
 * A studio clip card with playback funcitonality and an options menu. Currently this represents
 * both succesfully rendered clips as well as clips that failed to render.
 */
const Complete = ({
  selected,
  setFocusedState,
  onNameChange,
  studioClipCompleteClasses: { classes },
  children,
  hideAvatar,
  hideClipBodyTop,
  previewClip,
  handleSave,
  playbackIconFilled,
}: CompleteProps) => {
  const { clip, actionMenuAnchor, setSrcFailedToLoad, setAudio } =
    useContext(StudioClipContext);
  const [clipToUse, setClipToUse] = useState<
    ClipObject | ClipObjectPending | undefined
  >(undefined);
  const [localSrcToUse, setLocalSrcToUse] =
    useState<ClipObject['localSrc']>(undefined);
  const editorText = useAppSelector(selectText);
  const [isClipSaved, setIsClipSaved] = useState<boolean | undefined>(false);
  const [eventTracking] = useEventsContext();

  useEffect(() => {
    /**
     * If StudioClip is being used in the context of clip previews, we use previewClip over clip from context
     */
    if (previewClip) {
      setClipToUse(previewClip);
      setIsClipSaved(previewClip.isSaved);
    } else {
      setClipToUse(clip);
    }
  }, [previewClip, clip]);

  useEffect(() => {
    // type-guard is needed to avoid a type error in the `useAudioPlayer` declaration
    const localSrc = clipToUse && 'localSrc' in clipToUse && clipToUse.localSrc;
    if (localSrc) setLocalSrcToUse(localSrc);
  }, [clipToUse, previewClip, clip]);

  // Audio
  const [isSeeking, setIsSeeking] = useState<boolean>(false);

  const [
    {
      state,
      progress,
      formattedPlaybackPosition,
      togglePlayback,
      seek,
      errorMessage,
    },
    audio,
  ] = useAudioPlayer({
    src:
      localSrcToUse ||
      (clipToUse && (clipToUse as ClipObject).src) ||
      undefined,
  });

  useEffect(() => {
    if (audio) setAudio(audio);
  }, [audio, setAudio, children]);

  // NOTE: the sliderValue and progress value are kept in "sync" but must remain seperate variables.
  const [sliderValue, setSliderValue] = useState<number>(progress);

  useEffect(() => {
    if (!isSeeking) setSliderValue(progress);
  }, [progress, isSeeking]);

  // Slider controls
  // Note: these event handlers are typed as any since we can technically get number or array of numbers
  // which is not compatible with only setting numbers in state
  const handleSliderChangeCommit = useCallback<
    SliderWrapperProps['onChangeCommitted']
  >(
    (_event, newValue) => {
      seek(newValue as number);
      setTimeout(() => {
        // Prevents jump/flicker of the slider when setSliderValue(progress) is called, gives the
        // audio element progress time to catch up
        setIsSeeking(false);
      }, 50);
    },
    [seek]
  );
  const handleSliderChange = useCallback<SliderWrapperProps['onChange']>(
    (_event, newValue) => {
      setSliderValue(newValue as number);
    },
    []
  );

  // Handler that adds keyboard support to the audio playback
  const handleSliderKeyDown: KeyboardEventHandler<HTMLSpanElement> =
    useCallback(
      event => {
        switch (event.keyCode) {
          case KEY_ENTER:
          case KEY_RETURN:
          case KEY_SPACE:
            togglePlayback();
            break;
          case KEY_LEFT:
          case KEY_RIGHT:
          case KEY_UP:
          case KEY_DOWN:
            if (audio) audio.pause();
            break;
          default:
            break;
        }
      },
      [audio, togglePlayback]
    );

  // Card focus state (if audio playing or menu open)
  useEffect(() => {
    if (state === 'PLAYING' || Boolean(actionMenuAnchor)) {
      setFocusedState(true);
    } else {
      setFocusedState(false);
    }
  }, [state, actionMenuAnchor, setFocusedState]);

  useEffect(() => {
    if (state === 'ERROR') setSrcFailedToLoad(true);
  }, [state, setSrcFailedToLoad]);

  let renderedError = null;
  if (clip?.failedWithError) {
    renderedError = clip.failedWithError;
  } else if (state === 'ERROR' && errorMessage) {
    renderedError = errorMessage;
  }

  const renderSaveBtn = Boolean(clipToUse && hideClipBodyTop);

  const playbackFn = () => {
    if (clipToUse) {
      togglePlayback({ shouldResetIfPaused: false });

      eventTracking.track({
        type: previewClip ? EventName.ClipPreviewPlay : EventName.ClipPlay,
        data: {
          actorId: clipToUse?.actorId,
          clipId: clipToUse?.id,
        },
      });
    }
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        {!hideAvatar && clipToUse && (
          <AvatarContainer classes={classes} clip={clipToUse} />
        )}
        {/* Play/Pause button (error icon for failed clip) */}
        <Playback
          name={clipToUse?.name}
          classes={classes}
          togglePlayback={playbackFn}
          selected={selected}
          playerState={state}
          clipErrorMessage={clipToUse?.failedWithError}
          playbackIconFilled={playbackIconFilled}
        />
      </Box>
      <div className={classes.clipBodyContainer}>
        {clipToUse && (
          <>
            <ClipBodyTop
              classes={classes}
              clip={clipToUse}
              playerState={state}
              renderedError={renderedError}
              onNameChange={onNameChange}
              formattedPlaybackPosition={formattedPlaybackPosition}
              hideClipBodyTop={hideClipBodyTop}
            />
            <SliderWrapper
              classes={classes}
              isSeeking={isSeeking}
              playerState={state}
              clip={clipToUse}
              sliderValue={sliderValue}
              onChange={handleSliderChange}
              onChangeCommitted={handleSliderChangeCommit}
              onMouseDown={() => setIsSeeking(true)}
              onKeyDown={handleSliderKeyDown}
            />
          </>
        )}
      </div>
      {renderSaveBtn && handleSave && (
        <Box className={classes.ctaButtonContainer}>
          <Button
            variant="secondary"
            onClick={() =>
              handleSave({
                clip: clipToUse as ClipObject,
                editorText,
                setIsClipSaved,
              })
            }
            size="small"
            disabled={isClipSaved}
            className={classes.ctaButton}
          >
            {isClipSaved ? 'Clip Saved' : 'Save as Clip'}
          </Button>
        </Box>
      )}
      <div className={classes.actionsContainer}>{children}</div>
    </>
  );
};

export default Complete;
